'use client';

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faLocationDot,
  faChevronDown,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import {
  useGetTerritoriesForStrategyQuery,
  useGetMyChainedStrategiesQuery,
} from 'src/api/redux';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import {
  useClickOutside,
  useDropdownPosition,
  useResizeClose,
} from 'src/hooks/common';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { CircularProgress, Tooltip } from 'src/components/PulseUI';
import { getTargetStrategyId } from 'src/utils/strategyHelpers';
import { TerritoryItem } from './components';

const StrategyTerritoryDropdown = () => {
  const log = useLogging();
  const { user } = useAuth();
  const {
    selectedTerritory,
    selectedChainIds,
    setSelectedTerritory,
    boundedTerritory,
    setBoundedTerritory,
  } = useStrategies(LOCATION.STRATEGY_CENTER);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { dropdownStyle, calculatePosition } = useDropdownPosition(
    buttonRef,
    400
  );

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside([buttonRef, dropdownRef], closeDropdown, isOpen);
  useResizeClose(closeDropdown, isOpen);

  // Territories are per-strategy, but the Center selects via `selectedChainIds`
  // (not `selectedStrategy`), so derive the target strategy id only when exactly
  // one strategy is selected (same pattern as StrategyList).
  const { data: chainedStrategies } = useGetMyChainedStrategiesQuery();
  const strategyId = useMemo(() => {
    if (selectedChainIds.length !== 1) return undefined;
    const selectedChain =
      chainedStrategies?.results?.find((s) => s.id === selectedChainIds[0]) ??
      null;
    const id = getTargetStrategyId(selectedChain);
    return id != null ? String(id) : undefined;
  }, [selectedChainIds, chainedStrategies?.results]);

  const {
    data: territories,
    isLoading,
    isFetching,
  } = useGetTerritoriesForStrategyQuery(
    {
      strategyId,
      page: 1,
      pageSize: 200,
    },
    {
      skip: !strategyId,
      pollingInterval: 120_000,
      skipPollingIfUnfocused: true,
    }
  );

  // Close dropdown on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Organize territories by ownership
  const organizedTerritories = useMemo(() => {
    if (!territories?.results || !user)
      return { myTerritories: [], otherTerritories: [] };

    const myTerritories: MedScout.Territory[] = [];
    const otherTerritories: MedScout.Territory[] = [];

    territories.results.forEach((territory) => {
      if (territory.owner?.id === user.id) {
        myTerritories.push(territory);
      } else {
        otherTerritories.push(territory);
      }
    });

    return { myTerritories, otherTerritories };
  }, [territories?.results, user]);

  // Reset territory when the selected strategy changes or clears
  useEffect(() => {
    if (!strategyId) {
      setSelectedTerritory(null);
      setBoundedTerritory(null);
    }
  }, [strategyId, setSelectedTerritory, setBoundedTerritory]);

  const handleSelectAllTerritories = () => {
    setSelectedTerritory(null);
    setBoundedTerritory(null);
    setIsOpen(false);
    log.event('Strategy territory all selected', {
      source: 'StrategyTerritoryDropdown',
      strategyId,
    });
  };

  const handleSelectTerritory = (territory: MedScout.Territory) => {
    const canonicalTerritory = territory.is_canonical
      ? territory
      : territories?.results?.find((t) => t.is_canonical);

    if (canonicalTerritory) {
      setSelectedTerritory(canonicalTerritory);
    }

    setBoundedTerritory(territory.is_canonical ? null : territory);
    setIsOpen(false);

    log.event('Strategy territory selected', {
      source: 'StrategyTerritoryDropdown',
      territoryId: territory.id,
      territoryName: territory.name,
      isCanonical: territory.is_canonical,
      isSubTerritory: territory.is_sub_territory,
      territoryStatus: territory.status,
      strategyId,
    });
  };

  const isDisabled = !strategyId;
  const disabledReason =
    selectedChainIds.length > 1
      ? 'Select a single strategy to filter by territory'
      : selectedChainIds.length === 0
        ? 'Select a strategy to filter by territory'
        : '';

  const activeTerritory = useMemo(() => {
    const currentId = (boundedTerritory || selectedTerritory)?.id;
    if (!currentId || !territories?.results) return null;
    return territories.results.find((t) => t.id === currentId) || null;
  }, [boundedTerritory, selectedTerritory, territories?.results]);

  const isAllSelected = !activeTerritory;
  const buttonLabel = activeTerritory?.name || 'All Territories';

  const handleClick = () => {
    if (isDisabled) return;
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <Tooltip
        content={disabledReason}
        showOnCondition={isDisabled}
        position="bottom"
      >
        <div data-testid="territory-dropdown">
          <button
            ref={buttonRef}
            data-testid="dropdown-button"
            type="button"
            onClick={handleClick}
            className={`p-2 @[1000px]/header:px-4 @[1000px]/header:py-2 rounded-full border inline-flex items-center gap-2 transition-colors ${
              isDisabled
                ? 'opacity-50 cursor-not-allowed bg-neutral-100 border-neutral-300'
                : 'cursor-pointer bg-neutral-100 border-neutral-300 hover:bg-neutral-200'
            }`}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={buttonLabel}
            disabled={isDisabled}
          >
            <Icon icon={faLocationDot} className="text-tertiary" />
            <span className="hidden @[1000px]/header:inline text-primary text-sm font-semibold">
              {buttonLabel}
            </span>
            <Icon icon={faChevronDown} className={`hidden @[1000px]/header:inline text-tertiary transition-all duration-300 ${isOpen ? '-rotate-90' : 'rotate-0'}`} />
          </button>
        </div>
      </Tooltip>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            data-testid="dropdown-menu"
            className="w-64 bg-white rounded-2xl border border-neutral-200 shadow-lg"
            style={dropdownStyle}
            role="listbox"
          >
            <div className="px-4 pt-4 pb-2">
              <div className="text-xs font-semibold text-tertiary">
                Territory
              </div>
            </div>

            <div className="max-h-[50vh] overflow-y-auto py-1">
              {isLoading || isFetching ? (
                <div className="flex items-center justify-center h-32">
                  <CircularProgress size="sm" color="neutral" />
                </div>
              ) : (
                <>
                  {/* All Territories option */}
                  <div
                    data-testid="territory-option-all"
                    className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-neutral-50 transition-colors"
                    role="option"
                    aria-selected={isAllSelected}
                    onClick={handleSelectAllTerritories}
                  >
                    <span className="text-sm text-primary">All Territories</span>
                    {isAllSelected && (
                      <Icon
                        icon={faCheck}
                        className="w-3.5 h-3.5 text-primary ml-auto flex-shrink-0"
                        data-testid="territory-check-all"
                      />
                    )}
                  </div>

                  {/* My Territories */}
                  {organizedTerritories.myTerritories.map((territory) => (
                    <TerritoryItem
                      key={territory.id}
                      territory={territory}
                      isSelected={activeTerritory?.id === territory.id}
                      onSelect={handleSelectTerritory}
                    />
                  ))}

                  {/* Direct Reports Territories */}
                  {organizedTerritories.otherTerritories.length > 0 && (
                    <>
                      {organizedTerritories.myTerritories.length > 0 && (
                        <div className="border-t border-neutral-200">
                          <div className="px-4 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                            Direct Reports
                          </div>
                        </div>
                      )}
                      {organizedTerritories.otherTerritories.map((territory) => (
                        <TerritoryItem
                          key={territory.id}
                          territory={territory}
                          isSelected={activeTerritory?.id === territory.id}
                          onSelect={handleSelectTerritory}
                        />
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default StrategyTerritoryDropdown;
